// src/components/ExamRichTextEditor.jsx
// Word-processor-style answer box for exam mode: a formatting toolbar
// (bold/italic/underline/lists/undo/redo) over a Tiptap editor, styled to
// resemble a CBE exam word processor tool. Emits HTML, which is what
// generateExamPdf.js parses back into a styled PDF at submit time.

import React, { useEffect, useRef, useState } from 'react';
import { useEditor, useEditorState, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import { TableKit } from '@tiptap/extension-table';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableMap } from '@tiptap/pm/tables';
import { rememberExamCopy, isExamCopy, PASTE_BLOCKED_MESSAGE } from '../utils/examClipboard';
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, Subscript as SubscriptIcon, Superscript as SuperscriptIcon, List, ListOrdered, Undo2, Redo2, RemoveFormatting, Scissors, Copy, ClipboardPaste, Table as TableIcon, Rows, Columns, Trash2, ZoomIn, ZoomOut } from 'lucide-react';

const TABLE_PICKER_MAX_ROWS = 6;
const TABLE_PICKER_MAX_COLS = 8;
const TABLE_CELL_MIN_WIDTH = 80; // must match the TableKit config below
const TABLE_COL_MIN_WIDTH = 40; // floor for the corner-resize handle - below this a column stops being usable
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.5;
const ZOOM_STEP = 0.1;

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Walks up from the current selection to find the enclosing table, if any.
// Returns { node, pos } where pos is the position immediately before the
// table node (ProseMirror's standard "pointing at this node" convention) -
// the same shape TableMap's own helpers expect a table's position in.
function findTableAtSelection(state) {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === 'table') {
      return { node, pos: $from.before(depth) };
    }
  }
  return null;
}

// Each column's current width, in order - from the first row's cell
// attributes (colwidth), falling back to the configured minimum for a
// column that's never been individually resized.
function getColumnWidths(tableNode) {
  const map = TableMap.get(tableNode);
  const widths = [];
  for (let c = 0; c < map.width; c++) {
    const relPos = map.positionAt(0, c, tableNode);
    const cell = tableNode.nodeAt(relPos);
    widths.push(cell?.attrs?.colwidth?.[0] || TABLE_CELL_MIN_WIDTH);
  }
  return widths;
}

// Commits new column widths to the actual document: for each column,
// selects every cell in it (top row to bottom row) via Tiptap's own
// setCellSelection, then setCellAttribute - which prosemirror-tables
// applies to every cell in a CellSelection, not just one - so all rows
// stay consistent, the same way dragging a single column border already
// keeps them consistent.
function applyColumnWidths(editor, tableInfo, newWidths) {
  const { node: tableNode, pos: tablePos } = tableInfo;
  const map = TableMap.get(tableNode);
  const tableStart = tablePos + 1;
  for (let c = 0; c < map.width; c++) {
    const anchorCell = tableStart + map.positionAt(0, c, tableNode);
    const headCell = tableStart + map.positionAt(map.height - 1, c, tableNode);
    editor.chain().setCellSelection({ anchorCell, headCell }).setCellAttribute('colwidth', [newWidths[c]]).run();
  }
  // Leave a normal cursor instead of the last column's selection highlight.
  editor.chain().setTextSelection(tableStart).run();
}

const ToolbarButton = ({ active, onClick, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    style={{
      padding: '6px 8px',
      background: active ? '#c33329' : 'white',
      color: active ? 'white' : '#4a5568',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      opacity: disabled ? 0.4 : 1
    }}
  >
    {children}
  </button>
);

const TableMenuAction = ({ icon: Icon, label, onClick, danger }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()} // keep focus/selection inside the table
    onClick={onClick}
    style={{
      display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
      padding: '6px 8px', background: 'none', border: 'none', borderRadius: '4px',
      cursor: 'pointer', fontSize: '13px', textAlign: 'left',
      color: danger ? '#c33329' : '#2c3e50'
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f6f9'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
  >
    <Icon size={14} />
    {label}
  </button>
);

const ExamRichTextEditor = ({ value, onChange, placeholder, disabled }) => {
  // Tiptap can fire onUpdate on its own during initial setup (e.g.
  // normalizing '' into '<p></p>', or a Placeholder decoration recompute) -
  // one or more of these land before the student has done anything. Gate
  // onChange on a real DOM focus instead of counting updates: every genuine
  // path in (typing, paste, or a toolbar button's chained .focus() call)
  // focuses the editor first, so this can't be fooled by however many
  // internal updates Tiptap fires on mount.
  const hasUserInteractedRef = useRef(false);
  const tableDragRef = useRef(null); // in-progress corner-drag state, see handleCornerMouseDownRef
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [zoom, setZoom] = useState(1); // visual magnification only - stored HTML/PDF output stays unaffected
  const zoomRef = useRef(1); // mirrors `zoom` for the table-drag mousemove listener below, mounted once with [] deps
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const [hoveredGrid, setHoveredGrid] = useState({ rows: 0, cols: 0 }); // insert-grid picker hover state
  const tableMenuRef = useRef(null);

  useEffect(() => {
    if (!showTableMenu) return;
    const handleOutsideClick = (e) => {
      if (tableMenuRef.current && !tableMenuRef.current.contains(e.target)) setShowTableMenu(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showTableMenu]);

  const editor = useEditor({
    extensions: [
      // Levels capped at 1-3 (not the default 1-6) - plenty for structuring
      // a discursive answer (e.g. a heading per requirement) without a
      // dropdown full of heading sizes no one needs here.
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Placeholder.configure({ placeholder: placeholder || 'Type your answer here...' }),
      CharacterCount,
      // cellMinWidth raised from the default 25px - with the CSS width:100%
      // override removed (see index.css), a freshly-inserted table's actual
      // rendered width is this value times the column count, so 25px would
      // make a new table look tiny before it's ever been resized.
      TableKit.configure({ table: { resizable: true, cellMinWidth: TABLE_CELL_MIN_WIDTH } }),
      // For referencing workings, e.g. "Profit¹" linking to a
      // "(1) Working 1..." note below - a standard ACCA answer technique.
      // Neither excludes the other by default, which lets text end up
      // both raised AND lowered at once (renders as nonsense) - configure
      // them as mutually exclusive, same as a real word processor.
      Subscript.extend({ excludes: 'superscript' }),
      Superscript.extend({ excludes: 'subscript' })
    ],
    content: value || '',
    editable: !disabled,
    editorProps: {
      handleDOMEvents: {
        focus: () => { hasUserInteractedRef.current = true; return false; }
      }
    },
    onUpdate: ({ editor }) => {
      if (!hasUserInteractedRef.current) return;
      onChange(editor.getHTML());
    }
  });

  // Tiptap v3's useEditor no longer re-renders this component on every
  // transaction (v2 did) - without this, every editor.isActive(...) read
  // below reflects whatever the LAST render's selection was, not the
  // current one, so the toolbar's pressed/active buttons (bold, italic,
  // underline, and the rest) visibly lag one click behind the real
  // selection. useEditorState subscribes to transactions itself and
  // re-renders this component whenever any of these derived values change.
  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      isBold: ctx.editor?.isActive('bold') ?? false,
      isItalic: ctx.editor?.isActive('italic') ?? false,
      isUnderline: ctx.editor?.isActive('underline') ?? false,
      isStrike: ctx.editor?.isActive('strike') ?? false,
      isSubscript: ctx.editor?.isActive('subscript') ?? false,
      isSuperscript: ctx.editor?.isActive('superscript') ?? false,
      isBulletList: ctx.editor?.isActive('bulletList') ?? false,
      isOrderedList: ctx.editor?.isActive('orderedList') ?? false,
      isHeading1: ctx.editor?.isActive('heading', { level: 1 }) ?? false,
      isHeading2: ctx.editor?.isActive('heading', { level: 2 }) ?? false,
      isHeading3: ctx.editor?.isActive('heading', { level: 3 }) ?? false,
      insideTable: ctx.editor?.isActive('table') ?? false,
      // Word count and undo/redo availability have the exact same
      // staleness problem as the isActive flags above (they were still
      // being read directly at render time even after this fix first
      // shipped) - included here so they update immediately too, e.g. Undo
      // no longer appears disabled right after an edit that hasn't yet
      // triggered some unrelated re-render.
      wordCount: ctx.editor?.storage.characterCount?.words() ?? 0,
      canUndo: ctx.editor?.can().undo() ?? false,
      canRedo: ctx.editor?.can().redo() ?? false
    })
  });

  // Keep editor content in sync if the value changes externally (e.g.
  // switching sections and back, or resuming an autosaved draft on load)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  // The resize handle is a real DOM element appended directly INTO the
  // table's own wrapper (the ".tableWrapper" div prosemirror-tables' own
  // TableView renders), positioned with plain CSS (right:0; bottom:0) -
  // the same technique ExamFloatingPanel's own corner handle uses, and for
  // the same reason: a handle that's an actual child of the box being
  // resized always tracks its corner exactly, for free, with no JS
  // measurement/repositioning code (and no risk of it drifting out of
  // sync) - unlike computing its pixel position from getBoundingClientRect
  // and re-rendering it as a separately-positioned React element.
  const attachedWrapperRef = useRef(null); // the .tableWrapper the handle is currently a child of, if any

  useEffect(() => {
    if (!editor) return;
    const handle = document.createElement('div');
    handle.title = 'Drag to resize the table (keeps column proportions)';
    Object.assign(handle.style, {
      position: 'absolute', right: '0', bottom: '0', width: '16px', height: '16px',
      cursor: 'nwse-resize',
      background: 'linear-gradient(135deg, transparent 50%, #cbd5e0 50%)',
      zIndex: '10'
    });
    handle.addEventListener('mousedown', (e) => handleCornerMouseDownRef.current(e));

    const syncHandlePosition = () => {
      const tableInfo = findTableAtSelection(editor.state);
      const wrapperDom = tableInfo && editor.view.nodeDOM(tableInfo.pos);
      if (wrapperDom === attachedWrapperRef.current) return;
      handle.remove();
      attachedWrapperRef.current = null;
      if (wrapperDom instanceof HTMLElement) {
        wrapperDom.style.position = 'relative';
        wrapperDom.appendChild(handle);
        attachedWrapperRef.current = wrapperDom;
      }
    };
    syncHandlePosition();
    editor.on('selectionUpdate', syncHandlePosition);
    editor.on('update', syncHandlePosition);
    return () => {
      editor.off('selectionUpdate', syncHandlePosition);
      editor.off('update', syncHandlePosition);
      handle.remove();
    };
  }, [editor]);

  // Drag-to-resize: mousedown on the handle captures each column's current
  // width; mousemove previews the scaled result directly via the DOM (cheap,
  // and avoids dispatching a transaction on every pixel of movement);
  // mouseup commits the real, proportionally-scaled widths to the document.
  useEffect(() => {
    const handleMove = (e) => {
      const drag = tableDragRef.current;
      if (!drag) return;
      // The table renders inside a CSS-zoomed wrapper (see below), so mouse
      // movement is reported in on-screen (post-zoom) pixels while column
      // widths are stored as real, pre-zoom px - dividing by the current
      // zoom converts the drag back to logical px.
      const delta = (e.clientX - drag.startX) / zoomRef.current;
      const scale = Math.max(0.3, (drag.startTotal + delta) / drag.startTotal);
      let previewTotal = 0;
      drag.colElements.forEach((col, i) => {
        const w = Math.max(TABLE_COL_MIN_WIDTH, Math.round(drag.startWidths[i] * scale));
        col.style.width = `${w}px`;
        previewTotal += w;
      });
      drag.tableEl.style.width = `${previewTotal}px`;
    };
    const handleUp = (e) => {
      const drag = tableDragRef.current;
      if (!drag) return;
      tableDragRef.current = null;
      document.body.style.cursor = '';
      const delta = (e.clientX - drag.startX) / zoomRef.current;
      const scale = Math.max(0.3, (drag.startTotal + delta) / drag.startTotal);
      const newWidths = drag.startWidths.map(w => Math.max(TABLE_COL_MIN_WIDTH, Math.round(w * scale)));
      applyColumnWidths(drag.editor, drag.tableInfo, newWidths);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []);

  // A ref (not a plain function) because the handle element above is
  // created once in a mount-time effect and needs a stable listener target
  // whose *body* can still see the latest `editor` on every render.
  const handleCornerMouseDownRef = useRef(() => {});
  handleCornerMouseDownRef.current = (e) => {
    e.preventDefault();
    const tableInfo = findTableAtSelection(editor.state);
    const wrapperDom = tableInfo && editor.view.nodeDOM(tableInfo.pos);
    const tableEl = wrapperDom?.querySelector('table');
    const colgroup = tableEl?.querySelector('colgroup');
    if (!tableInfo || !tableEl || !colgroup) return;
    const startWidths = getColumnWidths(tableInfo.node);
    tableDragRef.current = {
      editor,
      tableInfo,
      startX: e.clientX,
      startWidths,
      startTotal: startWidths.reduce((a, b) => a + b, 0),
      tableEl,
      colElements: Array.from(colgroup.children)
    };
    document.body.style.cursor = 'nwse-resize';
  };

  if (!editor) return null;

  // Cut/Copy/Paste toolbar buttons, for the same reason as the spreadsheet's
  // - go through navigator.clipboard since a button click has no native
  // copy/paste event to hook into, but native Ctrl+C/X/V and right-click
  // already work regardless, same as any normal text field.
  const handleCopyButton = async () => {
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, '\n');
    if (!text) return;
    rememberExamCopy(text);
    try { await navigator.clipboard.writeText(text); }
    catch (err) { console.error('Copy failed:', err); }
  };

  const handleCutButton = async () => {
    const { from, to } = editor.state.selection;
    const text = editor.state.doc.textBetween(from, to, '\n');
    if (!text) return;
    rememberExamCopy(text);
    try {
      await navigator.clipboard.writeText(text);
      editor.chain().focus().deleteSelection().run();
    } catch (err) { console.error('Cut failed:', err); }
  };

  const handlePasteButton = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      if (!isExamCopy(text)) { alert(PASTE_BLOCKED_MESSAGE); return; }
      const html = text.split(/\r?\n/).map(line => `<p>${escapeHtml(line) || ' '}</p>`).join('');
      editor.chain().focus().insertContent(html).run();
    } catch (err) {
      alert("Couldn't read from the clipboard - try Ctrl+V (or Cmd+V) instead, or check this site has clipboard permission in your browser.");
    }
  };

  const currentBlockLabel = editorState.isHeading1 ? 'h1'
    : editorState.isHeading2 ? 'h2'
    : editorState.isHeading3 ? 'h3'
    : 'paragraph';

  const setBlockType = (value) => {
    if (value === 'paragraph') editor.chain().focus().setParagraph().run();
    else editor.chain().focus().setHeading({ level: Number(value.slice(1)) }).run();
  };

  const insideTable = editorState.insideTable;

  const insertTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setShowTableMenu(false);
    setHoveredGrid({ rows: 0, cols: 0 });
  };

  const runTableAction = (action) => {
    editor.chain().focus()[action]().run();
    setShowTableMenu(false);
  };

  const wordCount = editorState.wordCount;

  return (
    <div style={{ border: '2px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        padding: '8px',
        background: '#f8f9fa',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <ToolbarButton disabled={disabled} title="Cut (Ctrl+X)" onClick={handleCutButton}>
          <Scissors size={15} />
        </ToolbarButton>
        <ToolbarButton disabled={disabled} title="Copy (Ctrl+C)" onClick={handleCopyButton}>
          <Copy size={15} />
        </ToolbarButton>
        <ToolbarButton disabled={disabled} title="Paste (Ctrl+V)" onClick={handlePasteButton}>
          <ClipboardPaste size={15} />
        </ToolbarButton>
        <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }} />
        <select
          value={currentBlockLabel}
          disabled={disabled}
          onChange={(e) => setBlockType(e.target.value)}
          title="Paragraph style"
          style={{ border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', color: '#3c4043', padding: '4px', cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <option value="paragraph">Paragraph</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
        <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }} />
        <ToolbarButton
          active={editorState.isBold}
          disabled={disabled}
          title="Bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editorState.isItalic}
          disabled={disabled}
          title="Italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editorState.isUnderline}
          disabled={disabled}
          title="Underline"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editorState.isStrike}
          disabled={disabled}
          title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editorState.isSubscript}
          disabled={disabled}
          title="Subscript"
          onClick={() => editor.chain().focus().toggleSubscript().run()}
        >
          <SubscriptIcon size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editorState.isSuperscript}
          disabled={disabled}
          title="Superscript (e.g. linking an answer to a working, like x¹)"
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
        >
          <SuperscriptIcon size={15} />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled}
          title="Clear formatting"
          onClick={() => {
            // A collapsed cursor (no highlighted text) has nothing for
            // unsetAllMarks to act on - it only affects the *selected*
            // range, so clicking with just a cursor placed would silently
            // do nothing. Expand to the current paragraph/heading first,
            // matching what a student clicking this while "in" a line of
            // text would actually expect to happen.
            const { selection } = editor.state;
            const range = selection.empty
              ? { from: selection.$from.start(), to: selection.$from.end() }
              : { from: selection.from, to: selection.to };
            editor.chain().focus().setTextSelection(range).clearNodes().unsetAllMarks().run();
          }}
        >
          <RemoveFormatting size={15} />
        </ToolbarButton>
        <div ref={tableMenuRef} style={{ position: 'relative' }}>
          <ToolbarButton
            active={insideTable}
            disabled={disabled}
            title="Table"
            onClick={() => setShowTableMenu(v => !v)}
          >
            <TableIcon size={15} />
          </ToolbarButton>
          {showTableMenu && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 20,
              padding: '10px', background: 'white', border: '1px solid #e2e8f0',
              borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
            }}>
              {insideTable ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '170px' }}>
                  <TableMenuAction icon={Rows} label="Add row above" onClick={() => runTableAction('addRowBefore')} />
                  <TableMenuAction icon={Rows} label="Add row below" onClick={() => runTableAction('addRowAfter')} />
                  <TableMenuAction icon={Trash2} label="Delete row" onClick={() => runTableAction('deleteRow')} />
                  <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                  <TableMenuAction icon={Columns} label="Add column left" onClick={() => runTableAction('addColumnBefore')} />
                  <TableMenuAction icon={Columns} label="Add column right" onClick={() => runTableAction('addColumnAfter')} />
                  <TableMenuAction icon={Trash2} label="Delete column" onClick={() => runTableAction('deleteColumn')} />
                  <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
                  <TableMenuAction icon={Trash2} label="Delete table" danger onClick={() => runTableAction('deleteTable')} />
                </div>
              ) : (
                <div>
                  <div
                    style={{ display: 'grid', gridTemplateColumns: `repeat(${TABLE_PICKER_MAX_COLS}, 18px)`, gap: '2px' }}
                    onMouseLeave={() => setHoveredGrid({ rows: 0, cols: 0 })}
                  >
                    {Array.from({ length: TABLE_PICKER_MAX_ROWS * TABLE_PICKER_MAX_COLS }, (_, i) => {
                      const r = Math.floor(i / TABLE_PICKER_MAX_COLS) + 1;
                      const c = (i % TABLE_PICKER_MAX_COLS) + 1;
                      const highlighted = r <= hoveredGrid.rows && c <= hoveredGrid.cols;
                      return (
                        <div
                          key={i}
                          onMouseEnter={() => setHoveredGrid({ rows: r, cols: c })}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => insertTable(r, c)}
                          style={{
                            width: '18px', height: '18px', border: '1px solid #d1d5db', borderRadius: '2px',
                            background: highlighted ? '#1a73e8' : 'white', cursor: 'pointer'
                          }}
                        />
                      );
                    })}
                  </div>
                  <div style={{ marginTop: '6px', fontSize: '12px', color: '#718096', textAlign: 'center' }}>
                    {hoveredGrid.rows > 0 ? `${hoveredGrid.rows} x ${hoveredGrid.cols}` : 'Insert table'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }} />
        <ToolbarButton
          active={editorState.isBulletList}
          disabled={disabled}
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={15} />
        </ToolbarButton>
        <ToolbarButton
          active={editorState.isOrderedList}
          disabled={disabled}
          title="Numbered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={15} />
        </ToolbarButton>
        <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }} />
        <ToolbarButton
          disabled={disabled || !editorState.canUndo}
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 size={15} />
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled || !editorState.canRedo}
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 size={15} />
        </ToolbarButton>
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#95a5a6', alignSelf: 'center' }}>
          {wordCount} word{wordCount !== 1 ? 's' : ''}
        </div>
        <div style={{ width: '1px', background: '#d1d5db', margin: '0 4px' }} />
        <ToolbarButton
          disabled={disabled || zoom <= ZOOM_MIN}
          title="Zoom out"
          onClick={() => setZoom(z => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100))}
        >
          <ZoomOut size={15} />
        </ToolbarButton>
        <div
          onClick={() => !disabled && setZoom(1)}
          title="Reset zoom to 100%"
          style={{ fontSize: '12px', color: '#4a5568', alignSelf: 'center', width: '40px', textAlign: 'center', cursor: disabled ? 'default' : 'pointer' }}
        >
          {Math.round(zoom * 100)}%
        </div>
        <ToolbarButton
          disabled={disabled || zoom >= ZOOM_MAX}
          title="Zoom in"
          onClick={() => setZoom(z => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100))}
        >
          <ZoomIn size={15} />
        </ToolbarButton>
      </div>
      <div style={{ overflowX: 'auto' }}>
        {/* zoom (not transform:scale) - it doesn't create a new containing
            block, so the corner-resize handle's position:absolute inside
            .tableWrapper still resolves against that wrapper, not this one.
            width:fit-content keeps this div sized to its actual (unzoomed)
            content instead of stretching to fill the scroll container, so
            zoom scales only the real content width, not empty trailing
            space too (the same stretched-wrapper bug fixed in index.css's
            .tableWrapper rule, applied here to the equivalent case). */}
        <div style={{ zoom, width: 'fit-content', minWidth: '100%' }}>
          <EditorContent
            editor={editor}
            style={{ padding: '16px', minHeight: '280px', fontSize: '14px', lineHeight: 1.6 }}
          />
        </div>
      </div>
    </div>
  );
};

export default ExamRichTextEditor;
